import React, {
	BaseSyntheticEvent,
	ChangeEvent,
	useEffect,
	useRef,
	useState,
} from 'react';

import ColorPicker from 'components/basic/color-picker/ColorPicker';
import { DatasetDropdown } from 'components/basic/dropdowns/dataset/DatasetDropdown';
import InputLabelWrapper from 'components/basic/input-label-wrapper/InputLabelWrapper';
import { Button } from 'primereact/button';
import { Checkbox, CheckboxChangeParams } from 'primereact/checkbox';
import { Column, ColumnBodyOptions } from 'primereact/column';
import { ConfirmPopup, confirmPopup } from 'primereact/confirmpopup';
import { DataTable } from 'primereact/datatable';
import { Dropdown, DropdownChangeParams } from 'primereact/dropdown';
import { FileUpload, FileUploadUploadParams } from 'primereact/fileupload';
import { Image } from 'primereact/image';
import { InputNumber } from 'primereact/inputnumber';
import { Toast } from 'primereact/toast';
import { Tooltip } from 'react-tooltip';
import { useScadaStore } from 'store/scada-store';
import { ChronoUnitLegacy } from 'utils/time-utils';
import { ImagesUploadResponse, ScadaLabel, ScadaProps } from '../scadaTypes';
import { IMAGE_UPLOAD_URL, getScadaImageUrl } from '../scadaUtils';

const defaultScadaConfig: ScadaProps = {
	scadaId: Date.now().toString(),
	size: 12,
	refreshRate: 30,
	maxDelayTime: 1,
	maxDelayTimeUnit: ChronoUnitLegacy.MINUTES,
	showScadaSettings: true,
	elements: [],
};

type ScadaEditorProps = {
	config: ScadaProps;
	onConfigChange: (config: ScadaProps) => void;
};

const ScadaEditor: React.FC<ScadaEditorProps> = ({
	config,
	onConfigChange,
}) => {
	const [currentConfig, setCurrentConfig] = useState(defaultScadaConfig);
	const toast = useRef<Toast>(null);
	const downloadRef = useRef<HTMLAnchorElement>(null);
	const scadaStore = useScadaStore();

	const scadaLabels = scadaStore.scadaMap.get(config.scadaId);
	const	scadaBgUrl = getScadaImageUrl(config.scadaBackgroundId);

	useEffect(() => {
			if (!scadaLabels) {
			onConfigChange(currentConfig);
			scadaStore.addScada({
				scadaId: config.scadaId ?? currentConfig.scadaId,
				labels: config.elements
			})
		} else {
			setCurrentConfig(prev => ({
				...prev,
				elements: scadaLabels
			}))
			onConfigChange({...config, elements: scadaLabels});
		}
	}, []);


	const handleConfigChange = (
		e:
			| ChangeEvent<HTMLInputElement>
			| BaseSyntheticEvent
			| DropdownChangeParams
			| CheckboxChangeParams,
	) => {
		const value =
			e.target.type === 'checkbox' ? e.target.checked : e.target.value;

		const newConfig = {
			...currentConfig,
			[e.target.name]: value,
		};

		setCurrentConfig(newConfig);
		onConfigChange(newConfig);
	};

	const updateElementsOnStates = (elements: ScadaLabel[]) => {
		const updConfig = {
			...currentConfig,
			elements,
		};
		scadaStore.updateScadaLabels(currentConfig.scadaId, elements);
		setCurrentConfig(updConfig);
		onConfigChange(updConfig);
	};

	const handleDatasetChange = (
		value: string | number,
		option: ColumnBodyOptions,
	) => {
		const elements = [...currentConfig.elements];
		const dataset = elements[option.rowIndex];
		const updDataset = {
			...dataset,
			[option.field]: value,
		};
		elements.splice(option.rowIndex, 1, updDataset);

		updateElementsOnStates(elements);
	};

	const confirmOnDelete = (
		e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
		index: number,
	) => {
		e.preventDefault();
		confirmPopup({
			target: e.currentTarget,
			message: 'Are you sure you want to delete this label?',
			icon: 'pi pi-exclamation-triangle',
			acceptClassName: 'p-button-danger',
			accept: () => deleteColumn(index),
		});
	};

	const deleteColumn = (index: number) => {
		const elements = [...currentConfig.elements];
		elements.splice(index, 1);
		updateElementsOnStates(elements);
	};

	const addLabel = (identifier: string | null, label: string | null) => {
		if (!identifier || !label) return;
		if (isIdentifierExists(identifier, label)) return;
		const elements: ScadaLabel[] = [
			...currentConfig.elements,
			{
				identifier,
				xCoord: 0,
				yCoord: 0,
				fontSize: 12,
				bgColor: '#f5f5f5',
				type: 'LABEL',
			},
		]

		updateElementsOnStates(elements);

		toast.current?.show({
			severity: 'success',
			summary: 'Success',
			detail: `Label "${label}" was added`,
		});
	};

	const updateLabel = (
		identifier: string | null,
		label: string | null,
		index: number,
	) => {
		if (!identifier || !label) return;
		if (isIdentifierExists(identifier, label)) return;
		const updatedElements = currentConfig.elements.map((e, i) => i === index ? {...e,identifier}: e);

		updateElementsOnStates(updatedElements);
		
		toast.current?.show({
			severity: 'success',
			summary: 'Updated',
			detail: `Label was updated to "${label}"`,
		});
	};

	const isIdentifierExists = (identifier:string, label: string) => {
		const identifiers = currentConfig.elements.map((e) => e.identifier);
		if (identifiers.includes(identifier)) {
			toast.current?.show({
				severity: 'warn',
				summary: 'Warn - label exists',
				detail: `Label "${label}" already exists in the list`,
			});
			return true;
		}
		return false;
}

	const onUpload = (e: FileUploadUploadParams) => {
		const response: ImagesUploadResponse = JSON.parse(e.xhr.response)

		const newConfig = {
			...currentConfig,
			scadaBackgroundId: response.id,
		};
		onConfigChange(newConfig);
		setCurrentConfig(newConfig);
		toast.current?.show({ severity: 'info', summary: 'Success', detail: 'Image Uploaded' });
	};

	const handleDownload = () => {
		downloadRef.current?.click();
	};

	return (
		<div className='grid p-fluid'>
			<ConfirmPopup />
			<Toast ref={toast} />
			<InputLabelWrapper labelName='Refresh rate in sec'>
				<InputNumber
					value={currentConfig.refreshRate}
					name='refreshRate'
					onChange={e => handleConfigChange(e.originalEvent)}
				/>
			</InputLabelWrapper>
			<InputLabelWrapper labelName='Not older than'>
				<div className='p-inputgroup'>
					<InputNumber
						value={currentConfig.maxDelayTime}
						name='maxDelayTime'
						onChange={e => handleConfigChange(e.originalEvent)}
						style={{ blockSize: '37px' }}
					/>
					<Dropdown
						value={currentConfig.maxDelayTimeUnit}
						options={Object.keys(ChronoUnitLegacy)}
						name='maxDelayTimeUnit'
						onChange={handleConfigChange}
						className='p-inputgroup-addon'
						style={{ blockSize: '37px' }}
					/>
				</div>
			</InputLabelWrapper>
			<InputLabelWrapper
				labelName='Show scada settings header'
				col={12}
				flexDirection='row-reverse'
				className='justify-content-end align-items-center gap-3'>
				<Checkbox
					checked={currentConfig.showScadaSettings}
					name='showScadaSettings'
					onChange={handleConfigChange}
					className='inline-block'
				/>
			</InputLabelWrapper>

			<InputLabelWrapper labelName='' col={4}>
				<FileUpload
					name='file'
					mode='basic'
					url={IMAGE_UPLOAD_URL}
					auto
					chooseOptions={{
						label: 'Upload Scada background',
						icon: 'pi pi-upload',
					}}
					onUpload={onUpload}
					accept='image/*'
				/>
			</InputLabelWrapper>
			{scadaBgUrl && (
				<InputLabelWrapper
					labelName=''
					col={8}
					className='justify-content-end gap-5'
					flexDirection='row'>
					<Image
						src={scadaBgUrl}
						alt='Scada background image'
						height='35'
						style={{ objectFit: 'contain' }}
						preview
					/>
					<Button
						onClick={handleDownload}
						type='button'
						label='Download'
						icon='pi pi-download'
						className='p-button-warning w-12rem'
					/>
					<a
						ref={downloadRef}
						style={{ display: 'none' }}
						href={`${scadaBgUrl}?download=${Math.random()}`}
					/>
				</InputLabelWrapper>
			)}
			<DataTable
				value={currentConfig.elements}
				size='small'
				className='w-full mt-3'>
				<Column
					field='identifier'
					header='Dataset'
					className='w-full gap-3'
					body={(data, option) => (
						<DatasetDropdown
							value={data.identifier}
							onChange={(id, label) => updateLabel(id, label, option.rowIndex)}
						/>
					)}
				/>
				<Column
					field='xCoord'
					header='Position X, %'
					className='text-center'
					body={(data, option) => (
						<InputNumber
							style={{ minWidth: '4rem' }}
							value={data.xCoord}
							onChange={e => handleDatasetChange(e.value!, option)}
							maxFractionDigits={1}
						/>
					)}
				/>
				<Column
					field='yCoord'
					header='Position Y, %'
					className='text-center'
					body={(data, option) => (
						<InputNumber
							style={{ minWidth: '4rem' }}
							value={data.yCoord}
							onChange={e => handleDatasetChange(e.value!, option)}
							maxFractionDigits={1}
						/>
					)}
				/>
				<Column
					field='fontSize'
					header='Font size'
					body={(data, option) => (
						<InputNumber
							className='justify-self-end'
							style={{ minWidth: '3rem' }}
							value={data.fontSize}
							onChange={e => handleDatasetChange(e.value!, option)}
						/>
					)}
				/>
				<Column
					field='bgColor'
					header='Bg color'
					body={(data, option) => (
						<ColorPicker
							value={data.bgColor}
							onChange={e => handleDatasetChange(e.target.value, option)}
						/>
					)}
				/>
				<Column
					style={{ width: '0' }}
					body={(_, options) => (
						<>
							<Tooltip id='delete-btn' />
							<Button
								data-tooltip-id='delete-btn'
								data-tooltip-content='Delete'
								icon='pi pi-trash'
								className='p-button-sm p-button-danger'
								onClick={e => confirmOnDelete(e, options.rowIndex)}
							/>
						</>
					)}
				/>
			</DataTable>
			<div className='flex mt-3 w-full'>
				<DatasetDropdown onChange={addLabel} />
			</div>
		</div>
	);
};

export default ScadaEditor;
